import { useEffect, useMemo, useRef, useState } from "react";
import { COUNTRIES } from "../data/countriesList";

const ISO_LABEL = new Map(COUNTRIES.map((c) => [c.value, c.label]));

/**
 * Country pills plus search field with predictive list from {@link COUNTRIES}.
 */
export default function WorkCountriesPillPicker({
  disabled,
  countryCodes,
  inputDraft,
  onDraftChange,
  onAddCode,
  onRemoveCode,
  maxCodes,
  inputId,
  placeholder = "Search countries",
  placeholderDisabled = "Upload proof first",
}) {
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const q = inputDraft.trim().toLowerCase();
  const excluded = useMemo(() => new Set(countryCodes || []), [countryCodes]);

  const suggestions = useMemo(() => {
    const pool = COUNTRIES.filter((c) => !excluded.has(c.value));
    if (!q) return pool.slice(0, 12);
    return pool
      .filter(
        (c) => c.label.toLowerCase().includes(q) || c.value.toLowerCase().includes(q)
      )
      .slice(0, 12);
  }, [q, excluded]);

  useEffect(() => {
    setHighlight(0);
  }, [q]);

  useEffect(() => {
    function onDocDown(e) {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, []);

  const pick = (iso) => {
    if ((countryCodes || []).length >= maxCodes) return;
    onAddCode(iso);
    setOpen(true);
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (suggestions.length === 0) return;
      setHighlight((h) => Math.min(suggestions.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const s = suggestions[highlight];
      if (s) pick(s.value);
    } else if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "Backspace" && !inputDraft && (countryCodes || []).length) {
      const last = countryCodes[countryCodes.length - 1];
      onRemoveCode(last);
    }
  };

  return (
    <div ref={wrapRef} className="profile-manage-country-pills-wrap">
      <span className="profile-manage-sr-only" id={`${inputId}-desc`}>
        Add countries from the list. Use arrow keys and Enter to pick a suggestion.
      </span>
      <div
        className={`profile-manage-country-pills-editor${disabled ? " is-disabled" : ""}`}
        role="group"
        aria-describedby={`${inputId}-desc`}
      >
        {(countryCodes || []).map((iso) => (
          <span key={iso} className="profile-manage-country-pill">
            <span className="profile-manage-country-pill-label">{ISO_LABEL.get(iso) || iso}</span>
            <button
              type="button"
              className="profile-manage-country-pill-remove"
              onClick={(e) => {
                e.stopPropagation();
                if (!disabled) onRemoveCode(iso);
              }}
              disabled={disabled}
              aria-label={`Remove ${ISO_LABEL.get(iso) || iso}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          id={inputId}
          type="text"
          className="profile-manage-country-pills-input"
          disabled={disabled}
          value={inputDraft}
          onChange={(e) => {
            onDraftChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={disabled ? placeholderDisabled : placeholder}
          autoComplete="off"
          spellCheck={false}
          aria-autocomplete="list"
          aria-expanded={open && suggestions.length > 0 ? "true" : "false"}
          aria-controls={`${inputId}-listbox`}
        />
      </div>
      {open && !disabled && suggestions.length > 0 ? (
        <ul
          id={`${inputId}-listbox`}
          className="profile-manage-country-suggest-list"
          role="listbox"
        >
          {suggestions.map((c, i) => (
            <li
              key={c.value}
              role="option"
              aria-selected={i === highlight}
              className={`profile-manage-country-suggest-item${i === highlight ? " is-highlighted" : ""}`}
              onMouseEnter={() => setHighlight(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(c.value);
              }}
            >
              <span className="profile-manage-country-suggest-label">{c.label}</span>
              <span className="profile-manage-country-suggest-iso">{c.value}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
